import { css } from '@emotion/react';
import { Typography, Link, CircularProgress } from '@mui/material';
import { Fragment, useLayoutEffect } from 'react';
import { useHistory, Link as RouterLink } from 'react-router-dom';

import { shouldBeEmail, shouldBePassword } from '@jebel/utils';

import { useAppAuth } from 'providers/useAppAuth';
import { PasswordField } from 'shared/components/form';
import { DividerOr } from 'shared/components/symbols';
import { Box, Button, Form, FormTextField, RouteLayout } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { useQueryParams, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

import { SocialButtons } from '../components/SocialButtons';

const NATIVE_API_URI = process.env.REACT_APP_NATIVE_APP_URI;

const containerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const formBlock = css`
  display: grid;
  gap: 1rem;
`;

const bottomLinkWrap = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const callbackContainerCSS = css`
  display: flex;
  min-height: 60vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
`;

interface RouteSearchParams {
  /**
   * The code to confirm social sign-in.
   * @see https://docs.amplify.aws/gen1/javascript/build-a-backend/auth/add-social-provider/#required-for-multi-page-applications-complete-social-sign-in-after-redirect
   */
  code?: string;

  /**
   * The callback to redirect to after successful login.
   */
  callback?: string;

  /**
   * The code challenge to use for the native handoff.
   */
  codeChallenge?: string;
}

type FormData = {
  email?: string;
  password?: string;
};

export function AuthPage() {
  const [params] = useQueryParams<RouteSearchParams>();

  const { hasSession, login, confirmSocialSignIn, createExchangeCode } = useAppAuth();
  const { push: navigate } = useHistory();
  const { showError } = useToast();

  useLayoutEffect(() => {
    if (params.code) {
      confirmSocialSignIn(params.code);
    }

    if (!hasSession) {
      return;
    }

    redirect();
  }, [params.code, hasSession]);

  const redirect = async () => {
    try {
      if (params.callback && params.codeChallenge && params.callback.startsWith(NATIVE_API_URI)) {
        const response = await createExchangeCode(params.codeChallenge);
        const url = new URL(params.callback);

        if (!response) {
          throw new Error('No response from when creating exchange code');
        }

        url.searchParams.set('code', response.code);
        url.searchParams.set('username', response.username);
        url.searchParams.set('expiredAt', response.expiredAt);

        // Redirect to the callback URL if it matches the allowed URI.
        window.location.href = url.toString();
        return;
      }
    } catch (err) {
      showError('Something goes wrong when validating your credentials, please try again.');
      recordError(err);
    }

    navigate(ROUTES.user.home.index);
  };

  const onSubmit = async ({ email, password }: FormData) => {
    if (!email || !password) {
      return;
    }

    try {
      await login({ email, password });
      redirect();
    } catch (err) {
      showError('Something goes wrong when validating your credentials, please try again.');

      if (err instanceof Error) {
        showError(err.message);
        return;
      }
    }
  };

  if (hasSession && params.callback) {
    return (
      <RouteLayout>
        <Box css={callbackContainerCSS}>
          <CircularProgress />

          <Typography variant="subtitle2" align="center">
            Connecting to your account
          </Typography>

          <Typography variant="body2" align="center" color="textSecondary">
            You will be redirected in a few seconds, please stand by...
          </Typography>
        </Box>
      </RouteLayout>
    );
  }

  return (
    <RouteLayout>
      <Box css={containerCSS}>
        <Typography variant="h2" align="center">
          Welcome! Login here
        </Typography>

        <Form oldCss={formBlock} onSubmit={onSubmit} validateOnBlur={false}>
          {({ isSubmitting }) => (
            <Fragment>
              <FormTextField
                label="Email Address"
                placeholder="myaccount@jebelapp.com"
                name="email"
                required
                inputMode="email"
                validate={shouldBeEmail}
              />

              <PasswordField
                name="password"
                label="Enter your password"
                placeholder="Enter your password"
                validate={shouldBePassword}
                autoComplete="password"
                required
              />

              <Button
                loading={isSubmitting}
                variant="contained"
                size="large"
                type="submit"
                color="secondary"
              >
                Login
              </Button>

              <DividerOr />

              <SocialButtons />

              <Box css={bottomLinkWrap}>
                <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
                  Forgot your password?&nbsp;
                </Typography>

                <Link component={RouterLink} to={ROUTES.public.recovery}>
                  Click here
                </Link>
              </Box>

              <Box css={bottomLinkWrap}>
                <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
                  No account?&nbsp;
                </Typography>

                <Link component={RouterLink} to={ROUTES.public.request}>
                  Request access
                </Link>
              </Box>
            </Fragment>
          )}
        </Form>
      </Box>
    </RouteLayout>
  );
}
